import React, { useCallback, useEffect, useState } from 'react';
import { FaBan, FaCheck, FaEye, FaPencilAlt, FaPlaneDeparture, FaPlus, FaTable, FaTimes, FaTrash, FaUndo } from 'react-icons/fa';
import { FinField, FinNumber } from '../../components/FinField';
import ReactSelect from '../../components/ReactSelect';
import { PO_AEREO, PO_NAO_SE_APLICA } from '../../components/CabecalhoModeloCliente';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';
import CabecalhoEventoAereo from '../../components/CabecalhoEventoAereo';
import ErpTemplate from '../../components/ErpTemplate';
import api from '../../services/api';
import notify from '../../services/notify';
import ConfirmarAcaoModal, { ObservacaoObrigatoria, avisoOpcoesNoEvento } from '../../components/ConfirmarAcaoModal';
import { ROTULO_SITUACAO_SOLICITACAO, COR_SITUACAO_SOLICITACAO, cuidaDoPedido, abrePedido } from '../../util/malhaAereo';
import { useAtendimentoAereo } from '../../components/AtendimentoAereo';
import '../../css/financeiro-parcela-pagar.css';

/**
 * Painel de Solicitações de Malha Aérea do evento — a porta de entrada do fluxo.
 *
 * Antes, "Solicitação de Aéreo" no Painel de Eventos abria direto o editor de
 * trechos, porque a solicitação não existia como registro: era a visão por evento
 * das linhas de trecho. Agora ela existe (`eventoaereosolicitacao`), e é por aqui
 * que se escolhe qual abrir.
 *
 * O painel aparece SEMPRE, mesmo com uma solicitação só: tela que muda de cara
 * conforme a quantidade de dado obriga o usuário a aprender dois caminhos.
 */
export default function CadastroEventoSolicitacoesAereo({ match }) {
    const idEvento = match.params.idEvento;
    const history = useHistory();

    const perfil = localStorage.getItem('perfil');
    const token = localStorage.getItem('token');

    // O RSVP e Eventos abrem, editam e enviam o pedido; Eventos também aprova a malha; o Aéreo responde.
    const podeAbrirSolicitacao = abrePedido(perfil);
    // Importação e opções do evento são do RSVP (mesmo recorte do back na Importação).
    const podeImportar = cuidaDoPedido(perfil);

    // Mesmo grupo que abre o pedido: o teto é orçamento do cliente. Quem não pode vê o
    // valor como texto — e o back recusa de todo jeito, porque esconder controle no front
    // nunca foi permissão.
    const podeEditarTkt = podeAbrirSolicitacao;
    const [decidindo, setDecidindo] = useState(null); // id do pedido com decisão em curso
    // Cancelar e reabrir avisam outro cargo: confirmam antes de rodar.
    const [confirmacao, setConfirmacao] = useState(null); // { s, acao }
    // Campos do corpo da confirmação. Começam limpos a cada abertura (`abrirConfirmacao`).
    const [observacao, setObservacao] = useState('');

    const [solicitacoes, setSolicitacoes] = useState([]);
    const [headerEvento, setHeaderEvento] = useState({});
    const [carregando, setCarregando] = useState(true);
    const [criando, setCriando] = useState(false);
    const [modalAberto, setModalAberto] = useState(false);
    const [tktMedio, setTktMedio] = useState(null);
    // A P.O. Aéreo do pedido: o status, obrigatório, que vira o padrão do P.O. Aéreo do cabeçalho da malha, e os
    // números das POs do evento escolhidas, que vão juntos como texto. NÃO SE APLICA não pede PO.
    const [poAereo, setPoAereo] = useState(null);
    const [posEscolhidas, setPosEscolhidas] = useState([]);
    const [posDoEvento, setPosDoEvento] = useState([]);
    // Edição do TKT médio na própria linha: id do pedido aberto para edição, e o valor
    // sendo digitado. `null` no valor é teto em branco, não zero.
    const [editandoTkt, setEditandoTkt] = useState(null);
    const [valorTkt, setValorTkt] = useState(null);
    const [salvandoTkt, setSalvandoTkt] = useState(false);
    // Os prazos de SLA da família Sanofi, em dias, ao lado do TKT médio: na Nova solicitação (`slaNovo`) e no editor da
    // linha (`valorSla`), junto com o teto. Opcionais: a operação ainda não fixou os prazos. Fora da família, nada.
    const SLA_VAZIO = { slaEmissaoDiasUteis: null, slaAntecedenciaDiasCorridos: null };
    const [slaNovo, setSlaNovo] = useState(SLA_VAZIO);
    const [valorSla, setValorSla] = useState(SLA_VAZIO);
    const familiaSanofi = !!headerEvento.familiaSanofi;

    const carregar = useCallback(() => {
        setCarregando(true);
        api({ method: 'get', action: '/eventoAereoSolicitacao', token, params: { evento: idEvento } })
            .then(res => setSolicitacoes(res.data.data ?? []))
            .catch(err => notify.apiError(err, 'Erro ao carregar as solicitações'))
            .finally(() => setCarregando(false));
    }, [idEvento, token]);

    // Atendimento do Aéreo: sem atender o evento, o Aéreo só lê. Assumido, a lista relê as travas.
    const atendimento = useAtendimentoAereo(idEvento, carregar);

    useEffect(() => {
        carregar();
        api({ method: 'get', action: '/eventoAereoCabecalho', token, params: { evento: idEvento } })
            .then(res => setHeaderEvento(res.data.data ?? {}))
            .catch(() => setHeaderEvento({}));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [carregar, idEvento]);

    // As POs do evento, para o multiselect da Nova solicitação: lidas ao abrir o modal.
    useEffect(() => {
        if (!modalAberto) return;
        api({ method: 'get', action: '/eventoPo', token, params: { evento: idEvento } })
            .then(res => setPosDoEvento(res.data.data ?? []))
            .catch(() => setPosDoEvento([]));
    }, [modalAberto, idEvento, token]);
    const opcoesPo = [...new Set(posDoEvento.map(p => String(p.numero ?? '').trim()).filter(Boolean))].map(n => ({ value: n, label: n }));

    // O TKT médio é pedido na abertura porque é o teto contra o qual as cotações vão
    // ser classificadas — sem ele, a exigência de aprovação do cliente por TKT nunca
    // dispara. Obrigatório, e maior que zero: teto zero não limita nada.
    const tktValido = (v) => typeof v === 'number' && v > 0;
    const podeAbrir = tktValido(tktMedio) && !!poAereo;
    const criar = () => {
        setCriando(true);
        api({
            method: 'post',
            action: '/eventoAereoSolicitacao',
            token,
            data: { evento: idEvento, tktMedio, poAereo, poAereoNumeros: poAereo === PO_NAO_SE_APLICA ? [] : posEscolhidas,
                ...(familiaSanofi ? slaNovo : {}) },
        })
            .then(res => {
                toast.success('Solicitação aberta');
                setModalAberto(false);
                setTktMedio(null);
                setPoAereo(null);
                setPosEscolhidas([]);
                setSlaNovo(SLA_VAZIO);
                // Quem abre a solicitação vai preenchê-la agora: abre direto a tela dela.
                const id = res.data?.data?.id;
                if (id) {
                    history.push('/cadastroEventoSolicitacaoAereo/' + id);
                    return;
                }
                carregar();
            })
            .catch(err => notify.apiError(err, 'Erro ao abrir a solicitação'))
            .finally(() => setCriando(false));
    };

    const fecharModal = () => {
        if (criando) return;
        setModalAberto(false);
        setTktMedio(null);
        setPoAereo(null);
        setPosEscolhidas([]);
        setSlaNovo(SLA_VAZIO);
    };

    // Os dias do SLA: inteiro, sem sinal, com a unidade da coluna da planilha da Sanofi.
    const campoSla = (valor, onDias, campo, extra = {}) => (
        <FinNumber decimalScale={0} allowNegative={false} thousandSeparator=""
            suffix={campo === 'slaEmissaoDiasUteis' ? ' dias úteis' : ' dias'}
            value={valor ?? ''} onValueChange={({ floatValue }) => onDias(floatValue ?? null)} {...extra} />
    );
    const diasSla = (v, campo) => (v === null || v === undefined || v === '' ? '—'
        : `${v} ${campo === 'slaEmissaoDiasUteis' ? 'dias úteis' : 'dias'}`);

    const excluir = (s) => {
        api({ method: 'delete', action: '/eventoAereoSolicitacao/' + s.id, token })
            .then(() => {
                toast.success('Solicitação excluída');
                carregar();
            })
            .catch(err => notify.apiError(err, 'Erro ao excluir a solicitação'));
    };

    // As duas telas de baixo são endereçadas pela SOLICITAÇÃO — é ela que delimita
    // os trechos e as malhas. Por isso não há atalho no cabeçalho deste painel: sem
    // uma solicitação escolhida, não há o que abrir.
    //
    // "Malhas" leva ao PAINEL de malhas do pedido, não direto à Importação: um pedido
    // pode receber mais de uma rodada de cotação, e é o painel que as lista.
    const irParaSolicitacao = (s) => history.push('/cadastroEventoSolicitacaoAereo/' + s.id);
    const irParaMalhas = (s) => history.push('/cadastroEventoMalhasAereo/' + s.id);
    const irParaImportacao = () => history.push('/cadastroEventoPortacaoAereo/' + idEvento);

    const dataCurta = (iso) => {
        if (!iso) return '—';
        const d = new Date(String(iso).replace(' ', 'T'));
        return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('pt-BR');
    };

    const moeda = (v) => {
        if (v === null || v === undefined || v === '') return '—';
        const n = Number(v);
        return isNaN(n) ? '—' : n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    };

    // --- TKT médio editável na linha ---
    //
    // Sem botão parado na célula: o valor É o alvo do clique, com sublinhado pontilhado
    // só no hover. O teto muda pouco e a coluna é de leitura na maior parte do tempo —
    // um ícone fixo por linha competiria com Trechos/Malhas, que são o que se clica aqui.

    const abrirEdicaoTkt = (s) => {
        setEditandoTkt(s.id);
        const v = s.tktMedio;
        setValorTkt(v === null || v === undefined || v === '' ? null : Number(v));
        const dias = (d) => (d === null || d === undefined || d === '' ? null : Number(d));
        setValorSla({ slaEmissaoDiasUteis: dias(s.slaEmissaoDiasUteis), slaAntecedenciaDiasCorridos: dias(s.slaAntecedenciaDiasCorridos) });
    };

    const cancelarEdicaoTkt = () => {
        if (salvandoTkt) return;
        setEditandoTkt(null);
        setValorTkt(null);
        setValorSla(SLA_VAZIO);
    };

    const salvarTkt = (s) => {
        setSalvandoTkt(true);
        // Obrigatório: não se apaga o teto por aqui, só se troca. O back recusa vazio.
        if (!tktValido(valorTkt)) {
            setSalvandoTkt(false);
            toast.error('Informe o TKT médio — ele é obrigatório.');
            return;
        }
        // Na família Sanofi, os prazos de SLA vão junto: o vazio limpa o prazo.
        api({ method: 'put', action: '/eventoAereoSolicitacao/' + s.id, token, data: { tktMedio: valorTkt, ...(familiaSanofi ? valorSla : {}) } })
            .then(() => {
                toast.success(familiaSanofi ? 'TKT médio e SLA atualizados' : 'TKT médio atualizado');
                setEditandoTkt(null);
                setValorTkt(null);
                setValorSla(SLA_VAZIO);
                carregar();
            })
            .catch(err => notify.apiError(err, 'Erro ao salvar o TKT médio'))
            .finally(() => setSalvandoTkt(false));
    };

    const celulaTktMedio = (s) => {
        if (editandoTkt !== s.id) {
            // `motivoNaoEditar` vem do back: pedido cancelado não troca de teto.
            if (!podeEditarTkt || s.motivoNaoEditar) return moeda(s.tktMedio);
            return (
                <button type="button" className="fin-pp__tktedit"
                    title="Clique para alterar o TKT médio desta solicitação"
                    onClick={() => abrirEdicaoTkt(s)}>
                    {moeda(s.tktMedio)}
                </button>
            );
        }

        // Na família Sanofi, o editor flutua da última coluna de SLA (a célula dela, na tabela): ancorado aqui, com os
        // dois prazos, ele passaria da borda esquerda da tabela.
        if (familiaSanofi) return moeda(s.tktMedio);
        return (
            <>
                <span style={{ visibility: 'hidden' }}>{moeda(s.tktMedio)}</span>
                {editorTkt(s)}
            </>
        );
    };

    // FLUTUA sobre a linha em vez de ocupar a célula: o editor é mais largo que a
    // coluna, e inline ele empurrava a tabela para o scroll horizontal — a linha
    // inteira se mexia para editar um campo. Ancorado à direita, cresce para a
    // esquerda por cima das colunas de leitura e some ao confirmar.
    const editorTkt = (s) => {
        return (
            <>
                {/* Enter grava e Esc desiste — quem edita na linha não quer tirar a mão do
                    teclado; os botões ficam para quem não sabe disso.

                    O ouvinte é do WRAPPER, não do input: o `NumericFormat` embrulha o
                    próprio `onKeyDown` e o Enter não chegava (medido — a tecla não fazia
                    nada). Aqui o evento borbulha do input, e de quebra o Esc passa a
                    valer também com o foco em qualquer um dos dois botões. */}
                <span className="fin-pp__tktedit-pop"
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); salvarTkt(s); }
                        if (e.key === 'Escape') { e.preventDefault(); cancelarEdicaoTkt(); }
                    }}>
                    <FinNumber
                        value={valorTkt}
                        onValueChange={({ floatValue }) => setValorTkt(floatValue ?? null)}
                        placeholder="TKT médio"
                        disabled={salvandoTkt}
                        autoFocus
                        style={{ width: 118, textAlign: 'right' }}
                    />
                    {/* Na família Sanofi, os prazos de SLA no mesmo editor: são as colunas ao lado. */}
                    {familiaSanofi && ['slaEmissaoDiasUteis', 'slaAntecedenciaDiasCorridos'].map(campo => (
                        <React.Fragment key={campo}>
                            {campoSla(valorSla[campo], (d) => setValorSla(v => ({ ...v, [campo]: d })), campo, {
                                placeholder: campo === 'slaEmissaoDiasUteis' ? 'SLA emissão' : 'SLA antecedência',
                                title: campo === 'slaEmissaoDiasUteis' ? 'SLA de emissão, em dias úteis' : 'SLA de antecedência do evento, em dias corridos',
                                disabled: salvandoTkt,
                                style: { width: 140, textAlign: 'right' },
                            })}
                        </React.Fragment>
                    ))}
                    <button type="button" className="fin-pp__btn fin-pp__btn--sm fin-pp__btn--primary"
                        title="Salvar (Enter)" disabled={salvandoTkt || !tktValido(valorTkt)} onClick={() => salvarTkt(s)}>
                        <FaCheck size={11} />
                    </button>
                    <button type="button" className="fin-pp__btn fin-pp__btn--sm"
                        title="Cancelar (Esc)" disabled={salvandoTkt} onClick={cancelarEdicaoTkt}>
                        <FaTimes size={11} />
                    </button>
                </span>
            </>
        );
    };

    const abrirConfirmacao = (s, acao) => {
        setObservacao('');
        setConfirmacao({ s, acao });
    };

    // Cancelar (encerra sem conclusão, avisa o Aéreo) e reabrir (cancelada ou concluída volta em
    // alteração, avisa o Aéreo). Enviar, notificar alteração e o pedir alteração do Aéreo são na tela
    // da solicitação. O back diz, por linha, se pode e por que não — a tela só mostra.
    const decidirPedido = (s, acao) => {
        setDecidindo(s.id);
        const data = { solicitacao: s.id, acao };
        // A observação não é gravada: vai no e-mail. O back exige no reabrir.
        if (acao === 'reabrir') data.observacao = observacao;
        api({ method: 'post', action: '/eventoAereoSolicitacaoDecisao', token, data })
            .then(() => {
                toast.success({
                    cancelar: 'Solicitação cancelada',
                    reabrir: 'Solicitação reaberta',
                }[acao] ?? 'Decisão registrada');
                carregar();
            })
            .catch(err => notify.apiError(err, 'Erro ao registrar a decisão'))
            .finally(() => {
                setDecidindo(null);
                setConfirmacao(null);
            });
    };

    // Valor fora da lista aparece cru e apagado, nunca como um estado válido: a conexão
    // roda com `strict => false`, então um valor errado chega até aqui.
    const pillSituacao = (s) => {
        const v = String(s.situacao ?? '').trim();
        if (v === '') return <span className="fin-pp__pill is-muted">—</span>;
        const rotulo = ROTULO_SITUACAO_SOLICITACAO[v];
        if (!rotulo) return <span className="fin-pp__pill is-muted" title="Situação desconhecida">{v}</span>;
        return (
            <span className="d-inline-flex align-items-center gap-1 flex-wrap">
                <span className={'fin-pp__pill ' + COR_SITUACAO_SOLICITACAO[v]}>{rotulo}</span>
                {/* Edição feita depois do envio, por fora da alteração formal. Só o grupo —
                    a pessoa fica na auditoria. */}
                {s.alteradoPor && (
                    <span className="fin-pp__pill is-warn" title="A solicitação ou o TKT médio mudou depois do envio ao Aéreo">
                        Alterado pelo {s.alteradoPor}
                    </span>
                )}
                {/* Opções das malhas desta solicitação já no evento. Não trava cancelar nem
                    pedir alteração: elas continuam no evento, e a confirmação avisa. */}
                {Number(s.portadas) > 0 && (
                    <span className="fin-pp__pill is-ok" title="O participante já vê estas opções. Cancelar ou pedir alteração não as retira do evento — isso é na Importação das Opções de Aéreo.">
                        {Number(s.portadas)} {Number(s.portadas) === 1 ? 'opção no evento' : 'opções no evento'}
                    </span>
                )}
            </span>
        );
    };

    const totalOrigens = solicitacoes.reduce((a, s) => a + (Number(s.origens) || 0), 0);
    const totalTrechos = solicitacoes.reduce((a, s) => a + (Number(s.trechos) || 0), 0);

    return (
        <ErpTemplate>
            <div className="main-content container-fluid fin-pp">

                <CabecalhoEventoAereo
                    titulo="Solicitações de Malha Aérea"
                    nomeEvento={headerEvento.nomeEvento}
                    cliente={headerEvento.cliente}
                    solicitante={headerEvento.solicitante}
                    dataEvento={headerEvento.dataEvento}
                    localEvento={headerEvento.localEvento}
                    voosSolicitados={[
                        `${totalOrigens} ${totalOrigens !== 1 ? 'origens' : 'origem'}`,
                        totalTrechos > 0 && `${totalTrechos} ${totalTrechos !== 1 ? 'trechos' : 'trecho'}`,
                    ].filter(Boolean).join(' · ')}
                    // Sem `tktMedio`: o painel é do evento, e cada solicitação tem o seu teto (na
                    // tabela). O cabeçalho esconde o bloco em vez de mostrar vazio.
                    atalhos={[
                        atendimento.atalho,
                        ...(podeImportar ? [
                            {
                                rotulo: 'Importação das Opções',
                                titulo: 'Importa as opções das malhas aprovadas para o participante escolher.',
                                to: '/cadastroEventoPortacaoAereo/' + idEvento,
                            },
                            {
                                rotulo: 'Opções de Aéreo',
                                titulo: 'Tela de cadastro das opções de aéreo do evento.',
                                to: '/cadastroEventoRsvpOpcaoAereo/' + idEvento,
                            },
                        ] : []),
                    ]}
                />

                <div className="fin-pp__head">
                    <div>
                        <h1 className="fin-pp__title">Solicitações</h1>
                        <p className="fin-pp__subtitle">
                            Cada solicitação agrupa as origens pedidas e as malhas recebidas.
                        </p>
                    </div>
                    {(podeAbrirSolicitacao || podeImportar) && (
                        <div className="fin-pp__actions">
                            {podeImportar && (
                                <button type="button" className="fin-pp__btn fin-pp__btn--pill"
                                    title="Abre a Importação das Opções de Aéreo: as opções das malhas aprovadas vão para o participante escolher."
                                    onClick={irParaImportacao}>
                                    <FaPlaneDeparture size={12} style={{ marginRight: 6 }} />
                                    Importar Opções de Aéreo
                                </button>
                            )}
                            {podeAbrirSolicitacao && (
                                <button type="button" className="fin-pp__btn fin-pp__btn--pill fin-pp__btn--primary"
                                    disabled={criando} onClick={() => setModalAberto(true)}>
                                    <FaPlus size={12} style={{ marginRight: 6 }} />
                                    Nova solicitação
                                </button>
                            )}
                        </div>
                    )}
                </div>

                <div className="fin-pp__card" style={{ overflow: 'hidden' }}>
                    <div className="fin-pp__tblwrap">
                        <table className="fin-pp__tbl" style={{ minWidth: 720, tableLayout: 'auto' }}>
                            <thead>
                                <tr>
                                    <th>Solicitação</th>
                                    <th>Situação</th>
                                    <th>Aberta em</th>
                                    <th>Aberta por</th>
                                    <th style={{ textAlign: 'right' }}>TKT médio</th>
                                    {familiaSanofi && (
                                        <>
                                            <th style={{ textAlign: 'right' }}>SLA emissão</th>
                                            <th style={{ textAlign: 'right' }}>SLA antecedência</th>
                                        </>
                                    )}
                                    <th style={{ textAlign: 'center' }}>Origens</th>
                                    <th style={{ textAlign: 'center' }}>Trechos</th>
                                    <th style={{ textAlign: 'center' }}>Malhas</th>
                                    <th style={{ width: 1 }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {carregando && (
                                    <tr><td colSpan={familiaSanofi ? 11 : 9} className="fin-pp__empty">Carregando...</td></tr>
                                )}
                                {!carregando && solicitacoes.length === 0 && (
                                    <tr><td colSpan={familiaSanofi ? 11 : 9} className="fin-pp__empty">
                                        Nenhuma solicitação neste evento.
                                    </td></tr>
                                )}
                                {!carregando && solicitacoes.map((s, i) => {
                                    const vazia = !Number(s.origens) && !Number(s.trechos) && !Number(s.malhas);
                                    return (
                                        <tr key={s.id}>
                                            <td className="fin-pp__cell-strong">
                                                <span className="fin-pp__cphead-ic" style={{ marginRight: 8 }}>
                                                    <FaPlaneDeparture size={13} />
                                                </span>
                                                Solicitação {i + 1}
                                                {/* O destino ao lado do número: "Solicitação 1" só
                                                    distingue pela ordem, e com duas ou três no
                                                    evento a única forma de saber qual é qual era
                                                    abrir. O destino já vem no pedido. */}
                                                {String(s.destino ?? '').trim() !== '' && (
                                                    <span className="fin-pp__cell-muted" style={{ fontWeight: 400 }}> · {s.destino}</span>
                                                )}
                                            </td>
                                            <td>{pillSituacao(s)}</td>
                                            <td className="fin-pp__cell-muted">{dataCurta(s.created_at)}</td>
                                            <td className="fin-pp__cell-muted">{s.usuarioNome || '—'}</td>
                                            {/* `relative`: âncora do editor flutuante do TKT médio. */}
                                            <td style={{ textAlign: 'right', position: 'relative' }} className="fin-pp__cell-muted">
                                                {celulaTktMedio(s)}
                                            </td>
                                            {/* Os prazos se editam no editor do TKT médio: o clique aqui o abre, e ele flutua da
                                                última destas colunas. */}
                                            {familiaSanofi && ['slaEmissaoDiasUteis', 'slaAntecedenciaDiasCorridos'].map(campo => (
                                                <td key={campo} style={{ textAlign: 'right', whiteSpace: 'nowrap', position: 'relative' }} className="fin-pp__cell-muted">
                                                    {editandoTkt === s.id && campo === 'slaAntecedenciaDiasCorridos' ? (
                                                        <>
                                                            <span style={{ visibility: 'hidden' }}>{diasSla(s[campo], campo)}</span>
                                                            {editorTkt(s)}
                                                        </>
                                                    ) : podeEditarTkt && !s.motivoNaoEditar && editandoTkt !== s.id ? (
                                                        <button type="button" className="fin-pp__tktedit"
                                                            title="Clique para alterar o TKT médio e os prazos de SLA desta solicitação"
                                                            onClick={() => abrirEdicaoTkt(s)}>
                                                            {diasSla(s[campo], campo)}
                                                        </button>
                                                    ) : diasSla(s[campo], campo)}
                                                </td>
                                            ))}
                                            <td style={{ textAlign: 'center' }}>
                                                <span className={'fin-pp__pill ' + (Number(s.origens) ? 'is-info' : 'is-muted')}>
                                                    {Number(s.origens) || 0}
                                                </span>
                                            </td>
                                            <td style={{ textAlign: 'center' }}>
                                                <span className={'fin-pp__pill ' + (Number(s.trechos) ? 'is-info' : 'is-muted')}>
                                                    {Number(s.trechos) || 0}
                                                </span>
                                            </td>
                                            <td style={{ textAlign: 'center' }}>
                                                <span className={'fin-pp__pill ' + (Number(s.malhas) ? 'is-ok' : 'is-muted')}>
                                                    {Number(s.malhas) || 0}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="d-flex align-items-center gap-2 justify-content-end">
                                                    {/* Escolha manual, depois do envio. Rascunho não cancela — exclui. */}
                                                    {!s.motivoNaoReabrir && (
                                                        <button type="button" className="fin-pp__btn fin-pp__btn--sm"
                                                            disabled={decidindo === s.id}
                                                            title="Reabre a solicitação cancelada ou concluída: volta em alteração, e avisa o Aéreo"
                                                            onClick={() => abrirConfirmacao(s, 'reabrir')}>
                                                            <FaUndo size={11} style={{ marginRight: 6 }} />
                                                            Reabrir
                                                        </button>
                                                    )}
                                                    {!s.motivoNaoCancelar && (
                                                        <button type="button" className="fin-pp__btn fin-pp__btn--sm fin-pp__btn--danger-ghost"
                                                            disabled={decidindo === s.id}
                                                            title="Encerra a solicitação sem conclusão e avisa o Aéreo"
                                                            onClick={() => abrirConfirmacao(s, 'cancelar')}>
                                                            <FaBan size={11} style={{ marginRight: 6 }} />
                                                            Cancelar
                                                        </button>
                                                    )}
                                                    {/* A solicitação inteira (origens e trechos): lápis para quem
                                                        edita, olho para quem só lê. Só com ícone, o botão ficava mais
                                                        baixo que os de texto: estica até a altura da linha. */}
                                                    <button type="button" className="fin-pp__btn fin-pp__btn--sm" style={{ alignSelf: 'stretch' }}
                                                        title={podeAbrirSolicitacao && !s.motivoNaoEditar ? 'Editar a solicitação' : 'Visualizar a solicitação'}
                                                        aria-label={podeAbrirSolicitacao && !s.motivoNaoEditar ? 'Editar a solicitação' : 'Visualizar a solicitação'}
                                                        onClick={() => irParaSolicitacao(s)}>
                                                        {podeAbrirSolicitacao && !s.motivoNaoEditar
                                                            ? <FaPencilAlt size={12} />
                                                            : <FaEye size={12} />}
                                                    </button>
                                                    {/* Rascunho não tem malha: ela só existe depois do envio ao Aéreo. */}
                                                    <button type="button" className="fin-pp__btn fin-pp__btn--sm"
                                                        disabled={s.situacao === 'pendente'}
                                                        title={s.situacao === 'pendente' ? 'Rascunho ainda não tem malha — ela começa depois do envio ao Aéreo.' : undefined}
                                                        onClick={() => irParaMalhas(s)}>
                                                        <FaTable size={12} style={{ marginRight: 6 }} />
                                                        Malhas
                                                    </button>
                                                    {podeAbrirSolicitacao && vazia && (
                                                        <button type="button" className="fin-pp__btn fin-pp__btn--sm fin-pp__btn--danger-ghost"
                                                            title="Excluir a solicitação (só enquanto está vazia)"
                                                            onClick={() => excluir(s)}>
                                                            <FaTrash size={12} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <div className="fin-pp__pagination">
                        <div className="fin-pp__pagination-info">
                            {solicitacoes.length} {solicitacoes.length !== 1 ? 'solicitações' : 'solicitação'}
                        </div>
                    </div>
                </div>


                {modalAberto && (
                    <div className="modal fade show d-block fin-pp fin-pp__modalbg"
                        onClick={(e) => { if (e.target === e.currentTarget) fecharModal(); }}>
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content fin-pp__modal">
                                <div className="fin-pp__modal-head">
                                    <span className="fin-pp__modal-head-ic"><FaPlaneDeparture size={17} /></span>
                                    <h3 className="fin-pp__modal-title">Nova solicitação
                                        <span className="fin-pp__modal-sub">
                                            O TKT médio é o teto que a classificação das cotações vai usar.
                                        </span>
                                    </h3>
                                    <button type="button" className="fin-pp__modal-close" onClick={fecharModal}>
                                        <FaTimes size={16} />
                                    </button>
                                </div>
                                {/* Enter abre a solicitação. O ouvinte é do wrapper, como no editor da
                                    linha: o `NumericFormat` embrulha o próprio `onKeyDown`. O Enter que o select
                                    usou para escolher uma opção (`defaultPrevented`) não abre. */}
                                <div className="modal-body"
                                    onKeyDown={(e) => {
                                        if (e.key !== 'Enter' || e.defaultPrevented) return;
                                        e.preventDefault();
                                        if (!criando && podeAbrir) criar();
                                    }}>
                                    <div className="fin-pp__filters">
                                        <FinField label="TKT médio" required>
                                            <FinNumber
                                                value={tktMedio}
                                                onValueChange={({ floatValue }) => setTktMedio(floatValue ?? null)}
                                                placeholder="Teto por passagem, na média"
                                            />
                                        </FinField>
                                        {/* Família Sanofi: os prazos de SLA ao lado do TKT médio, opcionais. */}
                                        {familiaSanofi && (
                                            <>
                                                <FinField label="SLA emissão">
                                                    {campoSla(slaNovo.slaEmissaoDiasUteis, (d) => setSlaNovo(v => ({ ...v, slaEmissaoDiasUteis: d })),
                                                        'slaEmissaoDiasUteis', { placeholder: 'Dias úteis' })}
                                                </FinField>
                                                <FinField label="SLA antecedência do evento">
                                                    {campoSla(slaNovo.slaAntecedenciaDiasCorridos, (d) => setSlaNovo(v => ({ ...v, slaAntecedenciaDiasCorridos: d })),
                                                        'slaAntecedenciaDiasCorridos', { placeholder: 'Dias corridos' })}
                                                </FinField>
                                            </>
                                        )}
                                        <FinField label="P.O. Aéreo" required>
                                            <ReactSelect options={PO_AEREO.filter(Boolean).map(v => ({ value: v, label: v }))}
                                                value={poAereo ? { value: poAereo } : null} placeholder="Status da P.O."
                                                onChange={e => setPoAereo(e ? e.value : null)} />
                                        </FinField>
                                        {poAereo && poAereo !== PO_NAO_SE_APLICA && (
                                            <FinField label="POs do evento" full>
                                                <ReactSelect isMulti options={opcoesPo}
                                                    value={opcoesPo.filter(o => posEscolhidas.includes(o.value))}
                                                    placeholder={opcoesPo.length ? 'Opcional' : 'O evento não tem PO cadastrada'}
                                                    onChange={sel => setPosEscolhidas((sel || []).map(o => o.value))} />
                                            </FinField>
                                        )}
                                    </div>
                                </div>
                                <div className="fin-pp__modal-foot">
                                    <button type="button" className="fin-pp__btn"
                                        disabled={criando} onClick={fecharModal}>
                                        Cancelar
                                    </button>
                                    <button type="button" className="fin-pp__btn fin-pp__btn--primary"
                                        disabled={criando || !podeAbrir} onClick={criar}>
                                        {criando ? 'Abrindo...' : 'Abrir solicitação'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {confirmacao && (() => {
                    const semObservacao = !observacao.trim();
                    const campoObservacao = <ObservacaoObrigatoria valor={observacao} onChange={setObservacao} />;
                    const cfg = {
                        cancelar: {
                            titulo: 'Cancelar a solicitação?',
                            subtitulo: 'Ela é encerrada sem conclusão e as malhas ficam só para consulta. Dá para reabrir depois por aqui.',
                            notifica: 'O Aéreo será notificado por e-mail.',
                            aviso: avisoOpcoesNoEvento(confirmacao.s.portadas, 'desta solicitação', 'Cancelar'),
                            // "Voltar" no desistir: com "Cancelar" os dois botões diriam a mesma palavra.
                            rotulo: 'Cancelar solicitação', rotuloFechar: 'Voltar', tom: 'danger', icone: 'danger',
                        },
                        reabrir: {
                            titulo: 'Reabrir a solicitação?',
                            subtitulo: 'Ela volta em alteração: o RSVP ajusta e reenvia ao Aéreo.',
                            notifica: 'O Aéreo será notificado por e-mail, com a observação.',
                            corpo: campoObservacao,
                            preso: semObservacao,
                            rotulo: 'Reabrir', tom: 'primary', icone: 'alteracao',
                        },
                    }[confirmacao.acao];
                    if (!cfg) return null;
                    return (
                        <ConfirmarAcaoModal
                            aberto
                            titulo={cfg.titulo}
                            subtitulo={cfg.subtitulo}
                            notifica={cfg.notifica}
                            aviso={cfg.aviso}
                            rotulo={cfg.rotulo}
                            rotuloFechar={cfg.rotuloFechar}
                            tom={cfg.tom}
                            icone={cfg.icone}
                            processando={decidindo === confirmacao.s.id}
                            confirmarPreso={!!cfg.preso}
                            onConfirmar={() => decidirPedido(confirmacao.s, confirmacao.acao)}
                            onFechar={() => setConfirmacao(null)}>
                            {cfg.corpo}
                        </ConfirmarAcaoModal>
                    );
                })()}

            </div>
            {atendimento.modal}
        </ErpTemplate>
    );
}
